import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  ArticleStatus,
  ArticleSummary,
  ArticleType,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { fetchArticles } from "@/lib/articles-api";
import {
  ARTICLE_STATUS_LABELS,
  ARTICLE_TYPE_LABELS,
  ARTICLE_TYPE_ORDER,
} from "@/lib/article-labels";

// CAR-119/120 — clinician content surface. Lists every editable article
// (lessons, checklists, education cards, coach messages) with its type,
// publish status and edit-version. The editor lives at /articles/:key.

function statusChip(status: ArticleStatus) {
  const cls =
    status === "published"
      ? "bg-success/10 text-success border-success/30"
      : "bg-warning/10 text-warning border-warning/30";
  return (
    <span
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${cls}`}
    >
      {ARTICLE_STATUS_LABELS[status]}
    </span>
  );
}

function shortDate(ts: string) {
  return ts.slice(0, 10);
}

export function ArticlesRoute() {
  const { signOut } = useAuth();
  const [rows, setRows] = useState<ArticleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<ArticleType | "">("");
  const [statusFilter, setStatusFilter] = useState<ArticleStatus | "">("");

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchArticles({
      type: typeFilter || undefined,
      status: statusFilter || undefined,
    })
      .then(setRows)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [typeFilter, statusFilter, signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Innehåll</h1>
        <Link
          to="/articles/new"
          className="rounded-md bg-brand text-white px-md py-sm text-sm font-medium hover:bg-brand/90 transition"
        >
          Ny artikel
        </Link>
      </div>

      <p className="text-sm text-textSecondary max-w-2xl">
        Lektioner, checklistor och annat innehåll som visas för patienten i
        appen. Ändringar i en publicerad artikel börjar gälla direkt. Spara som
        utkast för att dölja den tills den publiceras.
      </p>

      <div className="flex flex-wrap items-end gap-md">
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Typ
          </span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ArticleType | "")}
            className="rounded-md border border-surfaceMuted bg-surface px-md py-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            <option value="">Alla typer</option>
            {ARTICLE_TYPE_ORDER.map((t) => (
              <option key={t} value={t}>
                {ARTICLE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Status
          </span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ArticleStatus | "")
            }
            className="rounded-md border border-surfaceMuted bg-surface px-md py-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            <option value="">Alla</option>
            <option value="draft">{ARTICLE_STATUS_LABELS.draft}</option>
            <option value="published">{ARTICLE_STATUS_LABELS.published}</option>
          </select>
        </label>
        <span className="text-sm text-textSecondary pb-sm ml-auto">
          {loading ? "Hämtar…" : rows ? `${rows.length} artiklar` : ""}
        </span>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga artiklar matchar filtret.
        </div>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                <th className="text-left px-md py-sm font-medium">Titel</th>
                <th className="text-left px-md py-sm font-medium">Nyckel</th>
                <th className="text-left px-md py-sm font-medium">Typ</th>
                <th className="text-left px-md py-sm font-medium">Status</th>
                <th className="text-left px-md py-sm font-medium">Version</th>
                <th className="text-left px-md py-sm font-medium">Ändrad</th>
                <th className="px-md py-sm" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                >
                  <td className="px-md py-sm font-medium">
                    {row.title.sv || row.title.en || "(utan titel)"}
                  </td>
                  <td className="px-md py-sm font-mono text-xs text-textSecondary">
                    {row.key}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {ARTICLE_TYPE_LABELS[row.type]}
                  </td>
                  <td className="px-md py-sm">{statusChip(row.status)}</td>
                  <td className="px-md py-sm text-textSecondary">
                    v{row.version}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {shortDate(row.updatedAt)}
                  </td>
                  <td className="px-md py-sm text-right">
                    <Link
                      to={`/articles/${encodeURIComponent(row.key)}`}
                      className="text-brand hover:underline text-sm font-medium"
                    >
                      Redigera →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
